import React, { useEffect, useRef } from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import reelData from "../../../public/reel-data.json";
import { ReelMetadata } from "../../types";

const data = reelData as ReelMetadata;

// Extract highlight phrases and split them into unique words
const highlightWords = Array.from(
  new Set(
    data.bulletTimings
      .flatMap((b) => b.highlights ?? [])
      .flatMap((h) => h.split(/\s+/))
      .map((w) => w.replace(/[.,/#!$%^&*;:{}=\-_`~()?"']/g, "").trim())
      .filter((w) => w.length > 0)
  )
);

const defaultWords = ["DATA", "SYS", "SYNC", "NODE", "CORE", "FLOW", "GRID", "NETWORK", "SECURE", "LOCK", "LOAD"];
const displayWords = highlightWords.length > 0 ? displayWordsArray() : defaultWords;

function displayWordsArray() {
  return highlightWords.map((w) => w.toUpperCase());
}

export const GridMotionBackground: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const colWidth = 200;
  const rowHeight = 120;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // 1. Dark background fill
    ctx.fillStyle = "#04050f"; // Ultra dark void
    ctx.fillRect(0, 0, width, height);

    // 2. Neon glow ambient spots
    const angle = (frame / 250) * Math.PI * 2;
    const cyanX = width * 0.25 + Math.sin(angle) * 100;
    const cyanY = height * 0.3 + Math.cos(angle) * 120;
    const cyanGlow = ctx.createRadialGradient(cyanX, cyanY, 50, cyanX, cyanY, 600);
    cyanGlow.addColorStop(0, "rgba(0, 242, 254, 0.06)");
    cyanGlow.addColorStop(1, "rgba(0, 242, 254, 0)");
    ctx.fillStyle = cyanGlow;
    ctx.fillRect(0, 0, width, height);

    const pinkX = width * 0.75 + Math.cos(angle * 0.8) * 120;
    const pinkY = height * 0.7 + Math.sin(angle * 1.2) * 150;
    const pinkGlow = ctx.createRadialGradient(pinkX, pinkY, 60, pinkX, pinkY, 700);
    pinkGlow.addColorStop(0, "rgba(255, 0, 127, 0.06)");
    pinkGlow.addColorStop(1, "rgba(255, 0, 127, 0)");
    ctx.fillStyle = pinkGlow;
    ctx.fillRect(0, 0, width, height);

    // 3. Grid Motion logic
    const currentMs = (frame / fps) * 1000;
    const activeBullet = data.bulletTimings.find(
      (b) => currentMs >= b.startMs && currentMs <= b.endMs
    );
    
    const activeHighlights = activeBullet
      ? (activeBullet.highlights ?? [])
          .flatMap((h) => h.split(/\s+/))
          .map((w) => w.replace(/[.,/#!$%^&*;:{}=\-_`~()?"']/g, "").trim().toLowerCase())
          .filter((w) => w.length > 0)
      : [];

    // Continuous diagonal movement: down and right
    const driftX = (frame * 1.2) % colWidth;
    const driftY = (frame * 0.7) % rowHeight;

    const colsCount = Math.ceil(width / colWidth) + 3;
    const rowsCount = Math.ceil(height / rowHeight) + 3;

    // Draw grid lines
    ctx.lineWidth = 0.5;
    ctx.strokeStyle = "rgba(255, 255, 255, 0.02)";

    // Vertical lines
    for (let c = -1; c < colsCount; c++) {
      const x = c * colWidth + driftX;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    // Horizontal lines
    for (let r = -1; r < rowsCount; r++) {
      const y = r * rowHeight + driftY;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Draw cells
    for (let c = -2; c < colsCount; c++) {
      for (let r = -2; r < rowsCount; r++) {
        const x = c * colWidth + driftX;
        const y = r * rowHeight + driftY;

        // Deterministic random generator for cell properties based on absolute grid coordinates
        // Using offsets to keep cells stable even when shifting
        const absCol = c + Math.floor((frame * 1.2) / colWidth) * -1;
        const absRow = r + Math.floor((frame * 0.7) / rowHeight) * -1;
        const cellSeed = Math.sin(absCol * 12.9898 + absRow * 78.233) * 43758.5453;
        const rand = cellSeed - Math.floor(cellSeed);

        // Distance from center to implement a nice radial fade-out
        const cellCenterX = x + colWidth / 2;
        const cellCenterY = y + rowHeight / 2;
        const distToCenter = Math.hypot(cellCenterX - width / 2, cellCenterY - height / 2);
        const maxDist = Math.hypot(width / 2, height / 2);
        // Grid is faded at the center (where foreground text resides) and edges
        const centerClarity = Math.max(0.45, Math.min(1.0, distToCenter / 450));
        const edgeFade = Math.max(0.3, 1.0 - distToCenter / (maxDist * 0.95));
        const opacity = centerClarity * edgeFade;

        if (opacity <= 0.01) continue;

        ctx.save();
        ctx.globalAlpha = opacity;

        // Option 1: Display Highlight Word (28% chance)
        if (rand < 0.28) {
          const wordIndex = Math.floor(rand * 100) % displayWords.length;
          const word = displayWords[wordIndex];
          const isHighlighted = activeHighlights.includes(word.toLowerCase());

          ctx.font = isHighlighted ? "900 28px var(--font-title)" : "600 20px var(--font-title)";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          if (isHighlighted) {
            // Neon glowing word
            const glowColor = rand > 0.5 ? "#ff007f" : "#00f2fe";
            ctx.shadowBlur = 12;
            ctx.shadowColor = glowColor;
            ctx.fillStyle = glowColor;
            ctx.fillText(word, cellCenterX, cellCenterY);
          } else {
            // Background drifting word - more legible
            ctx.fillStyle = "rgba(255, 255, 255, 0.26)";
            ctx.fillText(word, cellCenterX, cellCenterY);
          }
        } 
        // Option 2: Technical decorative crosshairs or patterns (10% chance)
        else if (rand >= 0.28 && rand < 0.38) {
          ctx.strokeStyle = "rgba(255, 255, 255, 0.06)";
          ctx.lineWidth = 1;
          const size = 6;
          
          // Draw a small crosshair '+'
          ctx.beginPath();
          ctx.moveTo(cellCenterX - size, cellCenterY);
          ctx.lineTo(cellCenterX + size, cellCenterY);
          ctx.moveTo(cellCenterX, cellCenterY - size);
          ctx.lineTo(cellCenterX, cellCenterY + size);
          ctx.stroke();
        }
        // Option 3: Small dotted cluster (10% chance)
        else if (rand >= 0.38 && rand < 0.48) {
          ctx.fillStyle = "rgba(0, 242, 254, 0.15)";
          const size = 2;
          const spacing = 6;
          // Draw 2x2 dot grid
          ctx.fillRect(cellCenterX - spacing/2 - size/2, cellCenterY - spacing/2 - size/2, size, size);
          ctx.fillRect(cellCenterX + spacing/2 - size/2, cellCenterY - spacing/2 - size/2, size, size);
          ctx.fillRect(cellCenterX - spacing/2 - size/2, cellCenterY + spacing/2 - size/2, size, size);
          ctx.fillRect(cellCenterX + spacing/2 - size/2, cellCenterY + spacing/2 - size/2, size, size);
        }
        // Option 4: Glowing grid intersection corner (8% chance)
        else if (rand >= 0.48 && rand < 0.56) {
          ctx.strokeStyle = "rgba(255, 0, 127, 0.2)";
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(x, y + 15);
          ctx.lineTo(x, y);
          ctx.lineTo(x + 15, y);
          ctx.stroke();
        }

        ctx.restore();
      }
    }

    // 4. Subtle scanlines overlay
    ctx.fillStyle = "rgba(0, 0, 0, 0.25)";
    for (let y = 0; y < height; y += 4) {
      ctx.fillRect(0, y, width, 1.5);
    }

  }, [frame, width, height, fps]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
